import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Pagination from '../../component/Admin/Pagination';
import { FaPlusCircle, FaTrashAlt, FaSearch, FaSort, FaSortUp, FaSortDown, FaCalendarAlt } from 'react-icons/fa';
import Loading from '../../component/Loading';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import { adminTable } from '../../component/Admin/adminTable';
import SortBar from '../../component/Admin/SortBar';

const HariLibur = ({ data }) => {
    const [listHariLibur, setListHariLibur] = useState([]);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loading, setLoading] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Search and Sort states
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    useEffect(() => {
        getListHariLibur();
        // eslint-disable-next-line
    }, [page, limit, selectedYear]);

    const getListHariLibur = () => {
        setLoading(true);
        axios.get(`api/hari-libur?page=${page}&limit=${limit}&tahun=${selectedYear}`, {
            withCredentials: true
        })
            .then((response) => {
                setMeta(response.data.meta);
                setListHariLibur(response.data.items);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Gagal!',
                    text: error.response?.data?.message || 'Terjadi kesalahan saat memuat data',
                    confirmButtonColor: '#d33'
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const selectPage = (value) => {
        setPage(value);
    };

    const nextPage = () => {
        if (page < meta.totalPages) {
            setPage(page + 1);
        }
    };

    const prevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const limitPage = (event) => {
        setLimit(Number(event.target.value));
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setModalData(null);
    };

    const handleClick = (id, event) => {
        if (event && event.target.closest('span')) {
            return;
        }
        
        const data = listHariLibur.filter(item => item.id_hari_libur === id);
        if (data.length > 0) {
            setModalData(data[0]);
            setModalIsOpen(true);
        } else {
            setModalData(null);
            setModalIsOpen(false);
        }
    };

    const handleDelete = async (id, event) => {
        event.stopPropagation();
        
        const result = await Swal.fire({
            title: 'Apakah Anda yakin?',
            text: "Data hari libur yang dihapus tidak dapat dikembalikan!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Ya, Hapus!',
            cancelButtonText: 'Batal'
        });

        if (!result.isConfirmed) {
            return;
        }
        
        setLoading(true);
        try {
            await axios.delete(`api/hari-libur/${id}`, {
                withCredentials: true
            });
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil!',
                text: 'Hari libur berhasil dihapus',
                confirmButtonColor: '#3085d6',
                timer: 2000,
                showConfirmButton: true
            });
            getListHariLibur();
        } catch (error) {
            console.error('Error deleting data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Gagal!',
                text: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        const formData = new FormData(event.target);
        const data = {
            tanggal: formData.get('tanggal'),
            nama: formData.get('nama'),
            keterangan: formData.get('keterangan') || null,
            is_nasional: formData.get('is_nasional') === 'true',
            tahun: parseInt(formData.get('tahun'), 10)
        };

        try {
            if (modalData) {
                await axios.patch(`api/hari-libur/${modalData.id_hari_libur}`, data, {
                    withCredentials: true
                });
                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil!',
                    text: 'Data hari libur berhasil diperbarui',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: true
                });
            } else {
                await axios.post('api/hari-libur', data, {
                    withCredentials: true
                });
                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil!',
                    text: 'Hari libur baru berhasil ditambahkan',
                    confirmButtonColor: '#3085d6',
                    timer: 2000,
                    showConfirmButton: true
                });
            }
            getListHariLibur();
            closeModal();
        } catch (error) {
            console.error('Error saving data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Gagal!',
                text: error.response?.data?.message || error.response?.data?.error || 'Terjadi kesalahan saat menyimpan data',
                confirmButtonColor: '#d33'
            });
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setModalData(null);
        setModalIsOpen(true);
    };

    const handleSort = (field) => {
        if (field == null) {
            setSortField(null);
            setSortDirection('asc');
            return;
        }
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredAndSortedHariLibur = useMemo(() => {
        let filtered = listHariLibur;

        if (searchTerm) {
            filtered = filtered.filter(item => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    item.nama?.toLowerCase().includes(searchLower) ||
                    item.keterangan?.toLowerCase().includes(searchLower) ||
                    new Date(item.tanggal).toLocaleDateString('id-ID').includes(searchLower)
                );
            });
        }

        if (sortField) {
            filtered = [...filtered].sort((a, b) => {
                let aValue, bValue;

                switch (sortField) {
                    case 'tanggal':
                        aValue = new Date(a.tanggal);
                        bValue = new Date(b.tanggal);
                        break;
                    case 'nama':
                        aValue = a.nama || '';
                        bValue = b.nama || '';
                        break;
                    case 'tahun':
                        aValue = a.tahun || 0;
                        bValue = b.tahun || 0;
                        break;
                    default:
                        return 0;
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return sortDirection === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                }

                return sortDirection === 'asc'
                    ? (aValue > bValue ? 1 : -1)
                    : (aValue < bValue ? 1 : -1);
            });
        }

        return filtered;
    }, [listHariLibur, searchTerm, sortField, sortDirection]);

    const getSortIcon = (field) => {
        if (sortField !== field) {
            return <FaSort className="ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc' 
            ? <FaSortUp className="ml-1 text-white" />
            : <FaSortDown className="ml-1 text-white" />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    return (
        <>
            {loading ? <Loading /> : null}

            <div className={adminTable.pageHeader}>
                <div>
                    <h1 className={adminTable.pageTitle}>
                        <FaCalendarAlt className="text-[#1B3C33]" /> Hari Libur
                    </h1>
                    <p className={adminTable.pageSubtitle}>Kelola hari libur nasional & khusus</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        value={selectedYear}
                        onChange={(e) => {
                            setSelectedYear(parseInt(e.target.value, 10));
                            setPage(1);
                        }}
                        className="px-2.5 py-1.5 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-[#1B3C33] focus:border-transparent"
                    >
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                    <button className={adminTable.btnPrimary} onClick={openAddModal} type="button">
                        <FaPlusCircle /> Tambah Hari Libur
                    </button>
                </div>
            </div>

            <div className={adminTable.searchToolbar}>
                <div className={adminTable.searchWrap}>
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Cari nama hari libur atau keterangan..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={adminTable.searchInput}
                    />
                </div>
                <SortBar
                    options={[
                        { value: 'tanggal', label: 'Tanggal' },
                        { value: 'nama', label: 'Nama' },
                        { value: 'tahun', label: 'Tahun' },
                    ]}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                />
            </div>

            <div>
                <div className={adminTable.list}>
                    {filteredAndSortedHariLibur.length > 0 ? (
                        filteredAndSortedHariLibur.map((item, index) => (
                            <div
                                key={item.id_hari_libur || index}
                                className={adminTable.listItemClickable}
                                onClick={(e) => handleClick(item.id_hari_libur, e)}
                            >
                                <div className={adminTable.listTop}>
                                    <div className="min-w-0 flex-1">
                                        <div className={adminTable.listTitle}>{item.nama}</div>
                                        <div className={adminTable.listSub}>{formatDate(item.tanggal)} · {item.is_nasional ? 'Nasional' : 'Khusus'}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => handleDelete(item.id_hari_libur, e)}
                                        className={`${adminTable.actionBtn} text-red-500 hover:bg-red-50`}
                                        title="Hapus"
                                    >
                                        <FaTrashAlt />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={adminTable.listEmpty}>
                            {searchTerm ? 'Data tidak ditemukan' : `Tidak ada hari libur untuk tahun ${selectedYear}`}
                        </div>
                    )}
                </div>

                <div className={`${adminTable.card} ${adminTable.desktopOnly}`}>
                    <div className={adminTable.scroll}>
                        <table className={adminTable.table}>
                            <thead className={adminTable.thead}>
                                <tr>
                                    <th className={adminTable.thCenter}>No.</th>
                                    <th className={adminTable.thSortable} onClick={() => handleSort('tanggal')}>
                                        <div className="flex items-center">Tanggal {getSortIcon('tanggal')}</div>
                                    </th>
                                    <th className={adminTable.thSortable} onClick={() => handleSort('nama')}>
                                        <div className="flex items-center">Nama {getSortIcon('nama')}</div>
                                    </th>
                                    <th className={adminTable.th}>Keterangan</th>
                                    <th className={adminTable.thSortable} onClick={() => handleSort('tahun')}>
                                        <div className="flex items-center">Tahun {getSortIcon('tahun')}</div>
                                    </th>
                                    <th className={adminTable.thCenter}>Nasional</th>
                                    <th className={adminTable.thCenter}>Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedHariLibur.length > 0 ? (
                                    filteredAndSortedHariLibur.map((item, index) => (
                                        <tr
                                            key={item.id_hari_libur || index}
                                            onClick={(e) => handleClick(item.id_hari_libur, e)}
                                            className={adminTable.trClickable}
                                        >
                                            <td className={adminTable.tdCenter}>{index + 1 + (page - 1) * limit}</td>
                                            <td className={adminTable.td}>{formatDate(item.tanggal)}</td>
                                            <td className={`${adminTable.td} font-medium`}>{item.nama}</td>
                                            <td className={adminTable.tdMuted}>{item.keterangan || '-'}</td>
                                            <td className={adminTable.td}>{item.tahun}</td>
                                            <td className={adminTable.tdCenter}>
                                                {item.is_nasional ? (
                                                    <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-green-100 text-green-800">Ya</span>
                                                ) : (
                                                    <span className="px-2 py-1 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600">Tidak</span>
                                                )}
                                            </td>
                                            <td className={adminTable.tdCenter}>
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleDelete(item.id_hari_libur, e)}
                                                    className={`${adminTable.actionBtn} text-red-500 hover:bg-red-50`}
                                                    title="Hapus"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className={adminTable.empty}>
                                            {searchTerm ? 'Data tidak ditemukan' : `Tidak ada hari libur untuk tahun ${selectedYear}`}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mt-3">
                    <Pagination
                        currentPage={page}
                        totalPages={meta.totalPages}
                        limitPage={limitPage}
                        selectPage={selectPage}
                        nextPage={nextPage}
                        prevPage={prevPage}
                    />
                </div>
            </div>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                contentLabel="Detail Modal"
                ariaHideApp={false}
                className="fixed w-screen inset-0 flex items-center justify-center h-screen p-4 bg-gray-500 bg-opacity-50"
                overlayClassName="fixed z-50 w-screen h-screen inset-0 bg-gray-800 bg-opacity-75"
            >
                <div className='shadow-lg rounded-lg bg-white md:w-4/5 w-full h-5/6 p-4'>
                    <div className='text-center pt-2 text-xl font-semibold'>
                        {modalData ? 'EDIT HARI LIBUR' : 'TAMBAH HARI LIBUR'}
                    </div>
                    <div className="bg-white h-5/6 overflow-y-auto rounded-lg max-w-full m-3">
                        <div className='md:w-4/5 w-full mx-auto'>
                            <div className="grid place-items-center">
                                <div className="w-full bg-white">
                                    <form id='form-hari-libur' className="mt-6" onSubmit={handleSubmit}>
                                        <label htmlFor="tanggal" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">Tanggal</label>
                                        <input 
                                            id="tanggal" 
                                            type="date" 
                                            name="tanggal" 
                                            className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner" 
                                            required 
                                            defaultValue={modalData ? formatDateInput(modalData.tanggal) : ''} 
                                        />

                                        <label htmlFor="nama" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">Nama Hari Libur</label>
                                        <input 
                                            id="nama" 
                                            type="text" 
                                            name="nama" 
                                            placeholder="Contoh: Hari Raya Waisak" 
                                            className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner" 
                                            required 
                                            defaultValue={modalData ? modalData.nama : ''} 
                                        />

                                        <label htmlFor="keterangan" className="block mt-2 text-xs font-semibold text-gray-600 uppercase">Keterangan (Opsional)</label>
                                        <textarea 
                                            id="keterangan" 
                                            name="keterangan" 
                                            placeholder="Keterangan tambahan..." 
                                            rows="3"
                                            className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
                                            defaultValue={modalData ? modalData.keterangan : ''} 
                                        />

                                        <div className="flex mt-2 justify-between gap-3">
                                            <span className="w-1/2">
                                                <label htmlFor="tahun" className="block text-xs font-semibold text-gray-600 uppercase">Tahun</label>
                                                <input 
                                                    id="tahun" 
                                                    type="number" 
                                                    name="tahun" 
                                                    placeholder="2024" 
                                                    className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner" 
                                                    required 
                                                    defaultValue={modalData ? modalData.tahun : selectedYear} 
                                                />
                                            </span>
                                            <span className="w-1/2">
                                                <label htmlFor="is_nasional" className="block text-xs font-semibold text-gray-600 uppercase">Libur Nasional</label>
                                                <select 
                                                    id="is_nasional" 
                                                    name="is_nasional" 
                                                    className="block w-full p-3 mt-2 text-gray-700 bg-gray-200 appearance-none focus:outline-none focus:bg-gray-300 focus:shadow-inner"
                                                    defaultValue={modalData ? (modalData.is_nasional ? 'true' : 'false') : 'true'}
                                                >
                                                    <option value="true">Ya</option>
                                                    <option value="false">Tidak</option>
                                                </select>
                                            </span>
                                        </div>

                                        <button 
                                            type="submit" 
                                            className="w-full py-3 mt-6 font-medium tracking-widest text-white uppercase bg-black shadow-lg focus:outline-none hover:bg-gray-900 hover:shadow-none"
                                        >
                                            Simpan
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <button 
                        onClick={closeModal} 
                        className="w-full py-2 px-4 bg-[#1B3C33] text-white rounded-lg mt-4"
                    >
                        Tutup
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default HariLibur;

